import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { gql, useApolloClient, useQuery } from "@apollo/client";
import {
  ElementRegistryProvider,
  FormFileTransportProvider,
  PageRenderConfigProvider,
} from "@eaco/forms-ui-web";

import { APP_MAPBOX_ACCESS_TOKEN } from "../../config";
import { makePublicPageFileTransport } from "@/lib/portalFileTransport";
import { pageElementRegistry } from "@/lib/pageElementRegistry";
import { PageReadOnly } from "./PageReadOnly";
import PasswordView from "./PasswordView";
import PoweredByEaco from "./PoweredByEaco";
import NotFoundView from "./NotFoundView";

/**
 * A page document someone has shared publicly — pages.eaco.me/{slug}.
 *
 * The server is the gate: an unpublished page and one that never existed both
 * resolve to null, and a password-gated page returns its name but no content
 * until the password verifies. So this view can render whatever it is handed
 * without deciding anything about access.
 */

const PUBLIC_PAGE_QUERY = gql`
  query publicPageDocument($publicSlug: String!, $password: String) {
    publicPageDocument(publicSlug: $publicSlug, password: $password) {
      publicSlug
      name
      icon
      requirePassword
      passwordValid
      ownerName
      ownerLogo {
        id
        url
      }
      content
      updatedAt
    }
  }
`;

/**
 * The first page's blocks. A page document is either flat or wrapped in a
 * `pages` container; the public view shows the document as one continuous
 * page, which is the first of them.
 *
 * ISM follows the same layout to decide which files this page may serve to a
 * reader with no session (PageDocumentService::attachmentOidsIn): only a file
 * a block here shows resolves. A change to which blocks are laid out, or a new
 * block type that shows a file or holds child blocks, needs the same change
 * there, or its files come back unavailable on a public page.
 */
function pageElements(content: any): any[] {
  const els: any[] = content?.elements ?? [];
  const container = els.find(el => el?.typeId === "pages");
  if (container) {
    return (container.elements ?? []).flatMap((pg: any) => pg?.elements ?? []);
  }
  const pageEls = els.filter(el => el?.typeId === "page");
  if (pageEls.length > 0) {
    return pageEls.flatMap((pg: any) => pg?.elements ?? []);
  }

  return els;
}

/** The owner's name and logo above the page, so a reader knows whose it is. */
const OwnerBar: React.FC<{ name?: string | null; logoUrl?: string | null }> = ({
  name,
  logoUrl,
}) => {
  if (!name && !logoUrl) return null;

  return (
    <div className="flex items-center gap-3 border-b border-border px-6 py-3">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt={name ?? ""}
          className="h-7 w-auto max-w-[160px] object-contain"
        />
      ) : (
        <span className="text-sm font-semibold">{name}</span>
      )}
    </div>
  );
};

const PageShareView: React.FC = () => {
  const { publicSlug } = useParams<{ publicSlug: string }>();
  const [password, setPassword] = useState<string | null>(null);
  const client = useApolloClient();
  // Files are asked for through the page, so they unlock with it: the
  // transport is rebuilt when the reader enters the password.
  const transport = useMemo(
    () => makePublicPageFileTransport(client, publicSlug ?? "", password),
    [client, publicSlug, password],
  );

  const { data, loading } = useQuery(PUBLIC_PAGE_QUERY, {
    variables: { publicSlug, password },
    skip: !publicSlug,
    fetchPolicy: "network-only",
  });

  const page = data?.publicPageDocument;
  const content = useMemo(() => {
    const raw = page?.content;
    if (!raw) return null;

    return typeof raw === "string" ? JSON.parse(raw) : raw;
  }, [page?.content]);

  if (loading && !page) {
    return <div className="min-h-screen bg-background" />;
  }
  if (!page) {
    return <NotFoundView />;
  }
  if (page.requirePassword && !content) {
    return (
      <PasswordView
        formName={page.name}
        noun="page"
        invalid={page.passwordValid === false}
        checking={loading}
        onSubmit={setPassword}
      />
    );
  }

  return (
    <FormFileTransportProvider value={transport}>
      <PageRenderConfigProvider
        value={{ mapboxToken: APP_MAPBOX_ACCESS_TOKEN }}
      >
        {/* The app installs the form registry at its root; page blocks are a
            different vocabulary, so this subtree gets the page one. */}
        <ElementRegistryProvider registry={pageElementRegistry}>
          <div className="flex min-h-screen flex-col bg-background">
            <OwnerBar name={page.ownerName} logoUrl={page.ownerLogo?.url} />
            <div className="flex-1">
              <PageReadOnly
                name={page.name ?? ""}
                meta={{
                  icon: page.icon ?? undefined,
                  ...(content?.meta ?? {}),
                }}
                elements={pageElements(content)}
              />
            </div>
            <PoweredByEaco />
          </div>
        </ElementRegistryProvider>
      </PageRenderConfigProvider>
    </FormFileTransportProvider>
  );
};

export default PageShareView;
